import moment from "moment-timezone";
import { CONFIG } from "./config";

let currentBusinessID = null;
let currentStripePK = null;

function truthy(v) {
  return v === true || v === "true" || v === 1 || v === "1";
}

function toBoundary() {
  return "Boundary-" + Math.random().toString(16).slice(2);
}

function formatPhoneNumber(raw) {
  if (!raw) return "";
  if (raw.startsWith("+")) return raw;
  let num = String(raw).replace(/\D+/g, "");
  if (num.startsWith("0")) num = "+61" + num.slice(1);
  return num.startsWith("+") ? num : "+" + num;
}

async function postB64(body) {
  const boundary = toBoundary();
  const base64Body =
    typeof window !== "undefined" && typeof window.btoa === "function"
      ? window.btoa(JSON.stringify(body))
      : Buffer.from(JSON.stringify(body), "utf8").toString("base64");

  const multipart =
    `--${boundary}\r\n` +
    `Content-Disposition: form-data; name="b64"\r\n\r\n` +
    `${base64Body}\r\n` +
    `--${boundary}--\r\n`;

  const resp = await fetch(CONFIG.apiUrl, {
    method: "POST",
    headers: { "Content-Type": `multipart/form-data; boundary=${boundary}` },
    body: multipart,
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`HTTP ${resp.status}${text ? ` – ${text}` : ""}`);
  }

  return resp.json();
}

export function getCurrentBusinessID() {
  return currentBusinessID;
}
export function getCurrentStripePK() {
  return currentStripePK;
}
export function setBusinessContext({ businessId, stripePk } = {}) {
  if (Number.isFinite(Number(businessId))) currentBusinessID = Number(businessId);
  if (typeof stripePk === "string" && stripePk.trim()) currentStripePK = stripePk.trim();
}

export async function getSitePasses(siteId) {
  const body = {
    cmd: "get-site-app-clip-instant-app-passes",
    site_id: String(siteId),
    tz: moment.tz.guess(),
  };

  const json = await postB64(body);

  if (json?.data?.business_id) currentBusinessID = Number(json.data.business_id);
  if (json?.data?.skey) currentStripePK = json.data.skey;

  return json;
}

export async function authenticateSitePass({ siteId, name, phoneE164 }) {
  const body = {
    cmd: "authenticate-site-pass",
    site_id: String(siteId),
    name: (name || "").trim(),
    phone: formatPhoneNumber(phoneE164),
    tz: moment.tz.guess(),
    business_id: currentBusinessID,
  };
  const json = await postB64(body);
  if (json?.data) {
    if ("auth" in json.data) json.data.auth = truthy(json.data.auth);
    if ("is_banned" in json.data) json.data.is_banned = truthy(json.data.is_banned);
  }
  return json;
}

export async function compare2FASitePass({ siteId, name, phoneE164, twoFa }) {
  const body = {
    cmd: "compare-2FA-site-pass",
    site_id: String(siteId),
    name: (name || "").trim(),
    phone: formatPhoneNumber(phoneE164),
    two_fa: String(twoFa || "").trim(),
    tz: moment.tz.guess(),
    business_id: currentBusinessID,
  };
  return postB64(body);
}

export async function checkUserPassesForRego({ siteId, phoneE164, rego }) {
  const body = {
    cmd: "check-user-passes-for-rego",
    site_id: String(siteId),
    phone: formatPhoneNumber(phoneE164),
    rego: String(rego || "").trim().toUpperCase(),
    tz: moment.tz.guess(),
    business_id: currentBusinessID,
  };
  return postB64(body);
}

export async function getSitePassPaymentIntent({
  siteId,
  phoneE164,
  passes, // [{id, count}]
  duration,
  total,
  currency = "aud",
}) {
  const body = {
    cmd: "get-site-pass-payment-intent",
    site_id: String(siteId),
    phone: formatPhoneNumber(phoneE164),
    passes: Array.isArray(passes) ? passes : [],
    duration: Number(duration || 0),
    total: Number(total || 0),
    currency,
    tz: moment.tz.guess(),
    business_id: currentBusinessID,
  };

  const json = await postB64(body);
  const data = json?.data || {};
  const intent = data?.intent || {};
  const clientSecret = intent?.client_secret;
  const transactionId = data?.transaction_id;

  if (!clientSecret || !transactionId) {
    const msg = data?.message || "Missing client_secret or transaction_id";
    throw new Error(msg);
  }

  return { clientSecret, transactionId };
}

export async function getSitePassExtendPaymentIntent({
  siteId,
  phoneE164,
  passes,
  duration,
  total,
  currency = "aud",
}) {
  const body = {
    cmd: "get-site-pass-extend-payment-intent",
    site_id: String(siteId),
    phone: formatPhoneNumber(phoneE164),
    passes: Array.isArray(passes) ? passes : [],
    duration: Number(duration || 0),
    total: Number(total || 0),
    currency,
    tz: moment.tz.guess(),
    business_id: currentBusinessID,
  };

  const json = await postB64(body);
  const data = json?.data || {};
  const intent = data?.intent || {};
  const clientSecret = intent?.client_secret;
  const transactionId = data?.transaction_id;

  if (!clientSecret || !transactionId) {
    const msg = data?.message || "Missing client_secret or transaction_id";
    throw new Error(msg);
  }

  return { clientSecret, transactionId };
}

export async function createSitePass({
  siteId,
  phoneE164,
  duration,
  transactionId,
  carRegistration,
  carAddress,
  extendPassId = 0,
}) {
  const body = {
    cmd: "create-site-pass",
    site_id: String(siteId),
    phone: formatPhoneNumber(phoneE164),
    duration: Number(duration || 0),
    transaction_id: Number(transactionId || 0),
    car_registration: (carRegistration || "").trim().toUpperCase(),
    car_address: (carAddress || "").trim(),
    tz: moment.tz.guess(),
    business_id: currentBusinessID,
  };

  if (Number(extendPassId) > 0) body.extend_pass_id = Number(extendPassId);

  return postB64(body);
}

export async function extendSitePass({
  phoneE164,
  duration,
  transactionId,
  carRegistration,
  carAddress,
  passId,
}) {
  const body = {
    cmd: "extend-site-pass",
    phone: formatPhoneNumber(phoneE164),
    duration: Number(duration || 0),
    transaction_id: Number(transactionId || 0),
    car_registration: (carRegistration || "").trim().toUpperCase(),
    car_address: (carAddress || "").trim(),
    pass_id: Number(passId || 0),
    tz: moment.tz.guess(),
    business_id: currentBusinessID,
  };

  return postB64(body);
}

export async function getCurrentPass({ passId }) {
  const body = {
    cmd: "get-current-pass",
    pass_id: Number(passId || 0),
    tz: moment.tz.guess(),
    business_id: currentBusinessID,
  };
  return postB64(body);
}

export async function addToAppleWallet({ passId }) {
  const body = {
    cmd: "generate-apple-wallet-wc-pass-web",
    pass_id: Number(passId || 0)
  };
  return postB64(body);
}

export async function addToGoogleWallet({ passId }) {
  const body = {
    cmd: "generate-google-wallet-wc-pass",
    id: Number(passId || 0)
  };
  return postB64(body);
}

export async function checkRegoNumber({ rego, state }) {
  const body = {
    cmd: 'check-rego-number',
    rego,
    state
  }
  return postB64(body);
}
